import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Heart, Shield, Gift } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function FreeAILifeCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Free AI Life Coach - VZMO | Best Free AI Coaching App 2025';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Try VZMO - the best free AI life coach. Get 7 days of unlimited coaching for career clarity, relationship guidance, and personal growth. No credit card required.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/compare/free-ai-life-coach');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/compare/free-ai-life-coach';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('free-ai-coach-comparison');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            100% Free Trial - No Credit Card Required
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Free AI Life Coach: Start Today
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Get 7 days of unlimited AI life coaching completely free. Career guidance, relationship advice,
            and personal growth support available 24/7.
          </p>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Gift className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Your Free Trial Includes:</h2>
          <div className="grid md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-6">
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Unlimited AI coaching conversations</span>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Private journaling with AI insights</span>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Career clarity tools</span>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Relationship coaching</span>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Personalized action plans</span>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <span>Weekly progress tracking</span>
            </div>
          </div>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
          <p className="mt-4 text-sm opacity-75">Cancel anytime. No credit card required.</p>
        </div>

        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
            How Free AI Life Coaches Compare
          </h2>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Platform</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Free Trial</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Credit Card</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Full Features</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="bg-green-50">
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">VZMO</td>
                    <td className="px-6 py-4 text-center text-sm font-semibold text-green-600">7 days</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-slate-900">TextMei</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-600">3 days</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-500">Required</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-900">Life Coach AI</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-600">None</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-500">Required</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-500">Pay First</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-slate-900">ChatGPT</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-600">Forever</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center text-sm text-slate-500">Limited</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-900">Replika</td>
                    <td className="px-6 py-4 text-center text-sm text-slate-600">Forever</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center text-sm text-slate-500">Very Limited</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Zap className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Instant Access</h3>
            <p className="text-slate-600">
              Sign up in 30 seconds and start your first coaching session immediately. No waiting, no approval process.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Shield className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Completely Private</h3>
            <p className="text-slate-600">
              Your conversations are encrypted and never shared. Use VZMO anonymously if you prefer.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Heart className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Built for Growth</h3>
            <p className="text-slate-600">
              Not just conversations - get structured plans, weekly check-ins, and progress tracking.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Try a Free AI Life Coach?</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                24/7 Availability Without the Cost
              </h3>
              <p className="text-slate-600">
                Traditional life coaches charge $100-300 per session and require scheduling weeks in advance.
                AI life coaching gives you instant access whenever you need support - at 2am or during your lunch break.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No Judgment, Complete Privacy
              </h3>
              <p className="text-slate-600">
                Share your deepest concerns without fear of judgment. Your AI coach provides objective guidance
                without bias, and your conversations are completely private and encrypted.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Perfect for Career and Relationship Questions
              </h3>
              <p className="text-slate-600">
                Whether you're considering a career change, navigating a difficult relationship, or working through
                a personal challenge, AI coaching helps you think through options and create actionable plans.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Try Before You Commit
              </h3>
              <p className="text-slate-600">
                With VZMO's 7-day free trial, you can fully experience AI life coaching without any financial commitment.
                See if it works for you before paying anything.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
